import * as React from 'react'
import { Link } from 'react-router-dom'
import { Plus, Users } from 'lucide-react'
import { useJobs, useStages } from '@/lib/queries.js'
import { useTenant } from '@/app/TenantProvider.jsx'
import { money, timeAgo } from '@/lib/format.js'
import { cn, fullName } from '@/lib/utils.js'
import { Button } from '@/components/ui/button.jsx'
import { PageHeader } from '@/components/ui/card.jsx'
import { Alert, EmptyState } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { NewJobDialog } from './NewJobDialog.jsx'

/**
 * The pipeline board — the home screen (Phase 1b screen 2).
 *
 * ── WHY THIS IS HOME ────────────────────────────────────────────────────────
 * The loop's centre of gravity is "what is happening right now", not a list of
 * people. A contractor opening this on a phone between jobs wants to see where
 * work stands, and the board is the only view that answers that in one glance.
 *
 * ── THE EMPTY BOARD IS THE IMPORTANT CASE (D7) ──────────────────────────────
 * A brand-new tenant has zero jobs and exactly seven stages. So the first thing
 * a paying customer ever sees is this screen with nothing in it, and it has to
 * read as *ready* rather than *broken*. That means the seven columns render
 * regardless, and the call to action points at the onboarding funnel — pasting
 * the form snippet is literally what starts the loop.
 *
 * ── MOBILE-FIRST (D8) ───────────────────────────────────────────────────────
 * Phone: a vertical accordion of stages, because seven side-by-side columns on
 * a 375px screen is seven unusable columns. Desktop: the real board, columns
 * scrolling horizontally. Both render the same cards from the same data — the
 * difference is layout, not content.
 *
 * Cards carry the client's name from the embedded `clients` select, so the
 * board costs exactly two requests no matter how many jobs it shows.
 */
export function PipelineBoard () {
  const { workspaceName } = useTenant()
  const stagesQuery = useStages()
  const jobsQuery = useJobs()
  const [creating, setCreating] = React.useState(false)

  const stages = stagesQuery.data ?? []
  const jobs = jobsQuery.data ?? []

  const byStage = React.useMemo(() => {
    const map = new Map(stages.map(s => [s.id, []]))
    const orphans = []
    for (const job of jobs) {
      const bucket = map.get(job.pipeline_stage_id)
      if (bucket) bucket.push(job)
      // A job whose stage was deleted would otherwise vanish from the product
      // entirely — invisible, not gone. Surfaced below instead.
      else orphans.push(job)
    }
    return { map, orphans }
  }, [stages, jobs])

  const isLoading = stagesQuery.isPending || jobsQuery.isPending
  const error = stagesQuery.error ?? jobsQuery.error

  return (
    <>
      <PageHeader
        title="Pipeline"
        description={jobs.length > 0
          ? `${jobs.length} open ${jobs.length === 1 ? 'job' : 'jobs'} across ${stages.length} stages`
          : workspaceName}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New job
          </Button>
        }
      />

      {error && (
        <Alert tone="danger" title="Could not load the board" className="mb-4">
          {error.message}
        </Alert>
      )}

      {isLoading
        ? <BoardSkeleton />
        : (
          <>
            {/* D7: the first-run prompt sits ABOVE the board, never INSTEAD of
                it. Replacing the columns with an empty state would make a new
                tenant's pipeline look absent rather than ready — the plan is
                explicit that a blank page reads as broken while empty columns
                read as waiting for work. */}
            {jobs.length === 0 && <FirstRunEmptyState className="mb-5" />}

            {byStage.orphans.length > 0 && (
              <Alert tone="warn" title="Some jobs are not on the board" className="mb-4">
                {byStage.orphans.length} job{byStage.orphans.length === 1 ? '' : 's'} sit in a stage
                that no longer exists. They are listed under “Unassigned” below.
              </Alert>
            )}

            <Board stages={stages} byStage={byStage.map} orphans={byStage.orphans} />
          </>
          )}

      <NewJobDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}

function Board ({ stages, byStage, orphans }) {
  const columns = [
    ...stages.map(stage => ({ key: stage.id, stage, jobs: byStage.get(stage.id) ?? [] })),
    ...(orphans.length
      ? [{ key: '__orphans', stage: { name: 'Unassigned', color: '94A3B8' }, jobs: orphans }]
      : [])
  ]

  return (
    <>
      {/* ---- phone: stacked, collapsible ------------------------------- */}
      <div className="space-y-2 lg:hidden">
        {columns.map(({ key, stage, jobs }) => (
          <details
            key={key}
            // Stages holding work start open; empty ones stay shut so the
            // screen opens on what actually needs attention rather than on
            // seven headers.
            open={jobs.length > 0}
            className="group overflow-hidden rounded-lg border border-line bg-surface"
          >
            <summary className="flex min-h-tap cursor-pointer list-none items-center gap-2.5 px-4 py-3">
              <StageDot color={stage.color} />
              <span className="flex-1 text-sm font-semibold text-ink">{stage.name}</span>
              <span className="tabular text-sm text-ink-muted">{jobs.length}</span>
              <svg
                className="size-4 text-ink-subtle transition-transform group-open:rotate-90"
                viewBox="0 0 16 16" fill="none" aria-hidden="true"
              >
                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </summary>
            <div className="space-y-2 border-t border-line bg-canvas p-2">
              {jobs.length === 0
                ? <p className="px-2 py-3 text-sm text-ink-subtle">Nothing here yet.</p>
                : jobs.map(job => <JobCard key={job.id} job={job} />)}
            </div>
          </details>
        ))}
      </div>

      {/* ---- desktop: the actual board --------------------------------- */}
      {/* Negative margin lets the columns bleed to the viewport edge so the
          horizontal scroll reads as a board rather than as a broken layout. */}
      <div className="-mx-4 hidden overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:block">
        <div className="flex min-w-max gap-3">
          {columns.map(({ key, stage, jobs }) => (
            <section key={key} className="flex w-72 shrink-0 flex-col rounded-lg bg-surface-sunken">
              <header className="flex items-center gap-2 px-3 py-2.5">
                <StageDot color={stage.color} />
                <h2 className="flex-1 truncate text-sm font-semibold text-ink">{stage.name}</h2>
                <span className="tabular text-xs text-ink-muted">{jobs.length}</span>
              </header>
              <div className="flex-1 space-y-2 px-2 pb-2">
                {jobs.length === 0
                  ? (
                    <p className="rounded-md border border-dashed border-line-strong px-3 py-6 text-center text-xs text-ink-subtle">
                      Nothing here yet
                    </p>
                    )
                  : jobs.map(job => <JobCard key={job.id} job={job} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}

/**
 * A job card.
 *
 * The whole card is the link, not a "view" affordance inside it — a 44px target
 * on a phone should be the card, not a word in it.
 */
function JobCard ({ job }) {
  const client = job.clients ? fullName(job.clients) : null
  return (
    <Link
      to={`/jobs/${job.id}`}
      className={cn(
        'block rounded-md border border-line bg-surface p-3 shadow-card',
        'transition-[border-color,box-shadow] duration-150',
        'hover:border-line-strong hover:shadow-raised'
      )}
      data-testid="job-card"
      data-job-number={job.job_number}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 flex-1 text-sm font-medium leading-snug text-ink">{job.title}</p>
        {job.priority !== 'normal' && job.priority !== 'low' && (
          <StatusBadge status={job.priority} />
        )}
      </div>

      {client && <p className="mt-1 truncate text-sm text-ink-muted">{client}</p>}

      <div className="mt-2.5 flex items-center justify-between gap-2">
        {/* Truncated rather than wrapped. Document numbers carry the tenant
            slug, so a long business name produces a long number — and a card
            that grows a line taller than its neighbours breaks the scan down
            the column, which is the only thing the board is for. */}
        <span className="tabular min-w-0 truncate font-mono text-xs text-ink-subtle" title={job.job_number}>
          {job.job_number}
        </span>
        {Number(job.estimated_value) > 0 && (
          <span className="tabular shrink-0 text-xs font-medium text-ink">{money(job.estimated_value)}</span>
        )}
      </div>

      {job.status !== 'active' && (
        <div className="mt-2"><StatusBadge status={job.status} /></div>
      )}

      <p className="mt-2 text-xs text-ink-subtle">{timeAgo(job.created_at)}</p>
    </Link>
  )
}

/** Stage colours are stored bare (`2563EB`), not as CSS hex. */
function StageDot ({ color }) {
  return (
    <span
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: `#${String(color ?? '94A3B8').replace('#', '')}` }}
      aria-hidden="true"
    />
  )
}

/**
 * First run (D7).
 *
 * The call to action is the form snippet, because that is what actually starts
 * the loop for a Starter tenant — they keep their existing website, and leads
 * begin arriving the moment the snippet is on it. Offering "add a client" as
 * the only next step would describe a CRM; this describes the product.
 */
function FirstRunEmptyState () {
  return (
    <EmptyState
      icon={Users}
      title="No jobs yet"
      description="Your pipeline is ready. The fastest way to fill it is to put your lead form on your website — every enquiry then lands here automatically."
      action={{ label: 'Set up your lead form', href: '/settings' }}
      secondaryAction={{ label: 'Add a client manually', href: '/clients' }}
    />
  )
}

function BoardSkeleton () {
  return (
    <div className="flex gap-3 overflow-hidden" aria-hidden="true">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="w-72 shrink-0 space-y-2 rounded-lg bg-surface-sunken p-2">
          <div className="skeleton h-5 w-28" />
          <div className="skeleton h-20 w-full" />
          <div className="skeleton h-20 w-full" />
        </div>
      ))}
    </div>
  )
}
